import React, { useState, useEffect, Fragment, useCallback } from "react";
import * as Yup from "yup";
import { Button, Container, Grid, Paper, Typography, FormHelperText, Box } from "@mui/material";
import { useHistory } from "react-router-dom";
import * as ContactingApi from "../../Requests/contactingAPIs";
import {
    CHUIDatePicker,
    CHUIShortTextfield,
    CHUIProgressBackdrop,
    useCHUIDarkModeContext,
} from "@connecthealth-ui/core";
import { Formik, Form, useFormikContext } from "formik";
import InputMask from "react-input-mask";

import queryString from "query-string";

import ReCAPTCHA from "react-google-recaptcha";

//sx styles
const questionStyle = {
    marginTop: "60px",
};

const formikStyle = {
    marginTop: "60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
};

export function LightValidation(props) {
    const [loading, setLoading] = useState(false);
    const [recaptchaValue, setRecaptchaValue] = useState("");

    const { prefersDarkMode } = useCHUIDarkModeContext();
    const { push } = useHistory();

    let postcodeValidatorRegex = RegExp(
        "^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$"
    );
    let urlPath = window.location.pathname;

    useEffect(() => {
        document.title = "Connect Health | Validation";
    }, []);

    const handleBlur = (e, formikProps) => {
        formikProps.handleBlur(e);
    };

    if (loading) {
        return (
            <Fragment>
                <CHUIProgressBackdrop color="secondary" />
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <Paper
                    sx={{
                        padding: {
                            xs: 0,
                            sm: 3,
                            md: 5,
                        },
                        paddingBottom: {
                            xs: 5,
                            md: 8,
                        },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: "center",
                            padding: 3,
                        }}
                    >
                        Hello
                    </Typography>
                    <Container maxWidth="md">
                        <Paper
                            sx={{
                                padding: 5,
                            }}
                            elevation={prefersDarkMode ? 2 : 1}
                        >
                            <Formik
                                sx={formikStyle}
                                initialValues={{
                                    postcode: "",
                                    dob: "",
                                    mobileNumber: "",
                                    emailAddress: "",
                                }}
                                validateOnChange={false}
                                validateOnBlur={false}
                                onSubmit={async (values, { setSubmitting }) => {
                                    setSubmitting(true);
                                    setLoading(true);

                                    let captchaResponse = await ContactingApi.verifyReCaptcha(recaptchaValue, "2");

                                    if (captchaResponse == "invalid") {
                                        push(`/Validation/Error?reason=error`);
                                        return;
                                    }

                                    switch (urlPath) {
                                        case "/Booking/Validate":
                                            let validCheck = await ContactingApi.getAppointmentBookingRequestPatient(
                                                values["dob"],
                                                values["postcode"],
                                                values["emailAddress"],
                                                values["mobileNumber"]
                                            );
                                            switch (validCheck) {
                                                case "valid":
                                                    push(`/Booking/OptInBooking`);
                                                    break;

                                                case "notFound":
                                                    push(`/Validation/Error?reason=notFound`);
                                                    break;

                                                case "error":
                                                    push(`/Validation/Error?reason=error`);
                                                    break;

                                                default:
                                                    push(`/Booking/OptInBookingInvalid?reason=${validCheck}`);
                                                    break;
                                            }
                                            break;

                                        case "/CarePlan/Validate":
                                            const value = queryString.parse(window.location.search);
                                            const uuid = value.UUID;

                                            let carePlan = await ContactingApi.getCarePlanBase64Pdf(
                                                values["dob"],
                                                values["postcode"],
                                                values["emailAddress"],
                                                values["mobileNumber"],
                                                uuid
                                            );

                                            switch (carePlan) {
                                                case "error":
                                                    push(`/Validation/Error?reason=error`);
                                                    break;

                                                case "notFound":
                                                    push(`/Validation/Error?reason=notFound`);
                                                    break;

                                                default:
                                                    localStorage.setItem("carePlan", carePlan["base64Pdf"][0]);
                                                    push(`/CarePlan/Confirmation?q=${btoa(carePlan["firstName"])}`);
                                                    break;
                                            }
                                            break;

                                        default:
                                            alert("Invalid URL");
                                            break;
                                    }

                                    setLoading(false);
                                }}
                                validationSchema={Yup.object().shape(
                                    {
                                        postcode: Yup.string()
                                            .required("This field is required.")
                                            .matches(postcodeValidatorRegex, "Postcode is invalid"),
                                        dob: Yup.date().required("This field is required"),
                                        mobileNumber: Yup.string().when("emailAddress", {
                                            is: (emailAddress) => !emailAddress,
                                            //length is 12 to include the space for formatting
                                            then: Yup.string()
                                                .length(12, "Phone number is invalid")
                                                .required("This field is required"),
                                        }),
                                        emailAddress: Yup.string().when("mobileNumber", {
                                            is: (mobileNumber) => !mobileNumber,
                                            then: Yup.string()
                                                .required("This field is required")
                                                .email("Email is invalid"),
                                        }),
                                    },
                                    ["emailAddress", "mobileNumber"]
                                )}
                            >
                                {(props) => (
                                    <Form>
                                        <Typography style={{ textAlign: "center" }}>
                                            <h2> Please fill in the form below so we can confirm your identity. </h2>
                                            <h3>
                                                Please ensure that the details you are entering are the same details
                                                your GP will have on record.
                                            </h3>
                                        </Typography>
                                        <Grid container sx={questionStyle}>
                                            <Grid item xs={11}>
                                                <Typography variant={"h6"} htmlFor="postcode" tabIndex="0">
                                                    Please enter your postcode.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography>
                                                    <CHUIShortTextfield
                                                        id="postcode"
                                                        name="postcode"
                                                        onBlur={(e) => handleBlur(e, props)}
                                                        value={props.values["postcode"]}
                                                        support={false}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Grid container sx={questionStyle}>
                                            <CHUIDatePicker
                                                id="dob"
                                                name="dob"
                                                question="Please enter your date of birth."
                                                onBlur={(e) => handleBlur(e, props)}
                                            />
                                        </Grid>
                                        <Typography sx={questionStyle} style={{ textAlign: "center" }}>
                                            <h3 name="mobOrEmail">
                                                Please enter your mobile number and/ or email address.
                                            </h3>
                                        </Typography>

                                        <Grid container>
                                            <Grid item xs={11}>
                                                <Typography variant={"h6"} htmlFor="mobileNumber" tabIndex="0">
                                                    Mobile number
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography>
                                                    <InputMask
                                                        mask="99999 999999"
                                                        value={props.values["mobileNumber"]}
                                                        onChange={props.handleChange}
                                                        onBlur={(e) => handleBlur(e, props)}
                                                    >
                                                        {() => (
                                                            <CHUIShortTextfield
                                                                value={props.values["mobileNumber"]}
                                                                id="mobileNumber"
                                                                name="mobileNumber"
                                                                support={false}
                                                            />
                                                        )}
                                                    </InputMask>
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Grid container sx={{ marginTop: "30px" }}>
                                            <Grid item xs={11}>
                                                <Typography variant={"h6"} htmlFor="emailAddress" tabIndex="0">
                                                    Email address
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography>
                                                    <CHUIShortTextfield
                                                        id="emailAddress"
                                                        name="emailAddress"
                                                        type="email"
                                                        onBlur={(e) => handleBlur(e, props)}
                                                        value={props.values["email"]}
                                                        support={false}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        {(props.values.phoneNumber != "" || props.values.emailAddress != "") &&
                                            props.values.postcode != "" &&
                                            props.values.dob != "" && (
                                                <Box
                                                    sx={{
                                                        marginTop: "80px",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <ReCAPTCHA
                                                        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                                                        onChange={(value) => setRecaptchaValue(value)}
                                                    />
                                                </Box>
                                            )}

                                        <Box sx={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                type="submit"
                                                disabled={
                                                    props.isSubmitting ||
                                                    (props.values.phoneNumber == "" &&
                                                        props.values.emailAddress == "") ||
                                                    props.values.postcode == "" ||
                                                    props.values.dob == "" ||
                                                    !recaptchaValue
                                                }
                                            >
                                                {props.isSubmitting ? "submitting" : "submit"}
                                            </Button>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Paper>
                    </Container>
                </Paper>
            </Fragment>
        );
    }
}
